import 'css/src/components/common/ProductUpdates';

import React from 'react';
import { useSelector } from 'react-redux';

import { AnnouncementCard } from 'js/src/components/common/AnnouncementCard';
import { ContentPlaceholderSingleLineText } from 'js/src/components/common/ui/ContentPlaceholder';
import {
    loadAnnouncements,
    loadAnnouncementsUnreadCount,
    markAnnouncementAsViewed,
    selectAllAnnouncements,
    selectAnnouncementsIsLoading,
    selectAnnouncementsUnreadCount,
} from 'js/src/data/announcements';
import type { Announcement } from 'js/src/data/announcements/announcement-types';
import type { ImmutableMap } from 'js/src/libs/consts';
import { useAppDispatch } from 'js/src/libs/RootState';

type AnnouncementItemProps = {
    announcement: ImmutableMap<Announcement>;
};

const AnnouncementItem: React.FC<AnnouncementItemProps> = React.memo(function AnnouncementItem({
    announcement,
}) {
    const dispatch = useAppDispatch();
    const id = announcement.get('id');
    const isViewed = announcement.get('is_viewed');

    React.useEffect(() => {
        if (!isViewed) {
            dispatch(markAnnouncementAsViewed(id));
        }
    }, [dispatch, id, isViewed]);

    return (
        <div className="product-updates__item">
            <AnnouncementCard announcement={announcement} />
        </div>
    );
});

const LoadingPlaceholder = () => (
    <div className="product-updates__loading">
        {[...Array(3)].map((_, i) => (
            <div key={i} className="product-updates__item product-updates__item--loading">
                <ContentPlaceholderSingleLineText className="product-updates__placeholder--w60" />
                <ContentPlaceholderSingleLineText className="mt-l5-8" />
                <ContentPlaceholderSingleLineText className="mt-l5-4 product-updates__placeholder--w80" />
            </div>
        ))}
    </div>
);

const EmptyState = () => (
    <div className="product-updates__empty">
        <div className="product-updates__empty-icon">
            <i className="material-symbols-outlined">campaign</i>
        </div>
        <p>No updates at the moment.</p>
        <p className="muted">Check back soon for new features and improvements!</p>
    </div>
);

type ProductUpdatesProps = {
    onLoad?: (unreadCount: number) => void;
};

export const ProductUpdates: React.FC<ProductUpdatesProps> = React.memo(function ProductUpdates({
    onLoad,
}) {
    const dispatch = useAppDispatch();
    const announcements = useSelector(selectAllAnnouncements);
    const isLoading = useSelector(selectAnnouncementsIsLoading);
    const unreadCount = useSelector(selectAnnouncementsUnreadCount);

    React.useEffect(() => {
        dispatch(loadAnnouncements({ currentUrl: window.location.pathname }));
    }, [dispatch]);

    React.useEffect(() => {
        if (onLoad) {
            onLoad(unreadCount);
        }
    }, [onLoad, unreadCount]);

    if (isLoading && announcements.size === 0) {
        return <LoadingPlaceholder />;
    }

    if (announcements.size === 0) {
        return <EmptyState />;
    }

    return (
        <div className="product-updates">
            {announcements.map((announcement) => (
                <AnnouncementItem key={announcement.get('id')} announcement={announcement} />
            ))}
        </div>
    );
});

export const useProductUpdatesUnreadCount = () => {
    const dispatch = useAppDispatch();
    const unreadCount = useSelector(selectAnnouncementsUnreadCount);

    React.useEffect(() => {
        dispatch(loadAnnouncementsUnreadCount());
    }, [dispatch]);

    return unreadCount;
};

export default ProductUpdates;
